import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HiOutlineLockClosed, HiOutlineUser } from "react-icons/hi";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { getApiErrorMessage } from "../utils/apiResponse";

const LoginPage = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Username and password are required.");
      return;
    }

    setSubmitting(true);
    try {
      await login({ username: username.trim(), password });
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-inter text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(16,185,129,0.18),_transparent_55%),radial-gradient(ellipse_at_bottom_right,_rgba(59,130,246,0.16),_transparent_45%)]" />
      <div className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 bottom-10 h-80 w-80 rounded-full bg-sky-500/20 blur-3xl" />

      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-6 py-12 lg:flex-row lg:items-center lg:gap-16">
        <div className="mb-10 max-w-xl lg:mb-0 lg:flex-1">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300/90">
            Commission of Integrity
          </p>
          <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            CMS Admin Console
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">
            Sign in to manage pages, sections, multilingual content, and media
            for the public website.
          </p>
        </div>

        <div className="w-full max-w-md lg:flex-1">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-white/10 bg-white/95 p-8 text-slate-900 shadow-2xl shadow-black/30 backdrop-blur"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
              <p className="mt-2 text-sm text-slate-500">
                Use your administrator credentials to continue.
              </p>
            </div>

            <label className="mb-4 block">
              <span className="mb-2 block text-sm font-medium text-slate-700">
                Username
              </span>
              <div className="relative">
                <HiOutlineUser className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/15"
                  placeholder="admin"
                />
              </div>
            </label>

            <label className="mb-6 block">
              <span className="mb-2 block text-sm font-medium text-slate-700">
                Password
              </span>
              <div className="relative">
                <HiOutlineLockClosed className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/15"
                  placeholder="••••••••••••"
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting || loading}
              className="inline-flex w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting || loading ? "Signing in…" : "Sign in"}
            </button>

            <p className="mt-6 text-center text-xs text-slate-500">
              <Link to="/" className="font-medium text-emerald-700 hover:underline">
                Back to public site
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
